/**
 * The navigation registry — fifteen destinations in five labelled groups plus
 * the console entry, transcribed from the UI/UX pack verbatim.
 *
 * The console's entries are not drawn from the platform's `nav_entries`
 * vocabulary at all: that vocabulary names browse destinations over a catalogue,
 * and a console navigates its own surfaces. Its entries are the `[x]` management
 * surfaces themselves, one entry per surface, grouped by subject — so adding an
 * entry means adding the catalog row whose surface it reaches, never editing
 * this list to taste.
 *
 * The URL never carries the pack's bucket segment: `moderation/comments.html` is
 * a file-organisation fact and `/comments` is the route.
 *
 * **«البلاغات» is not here, and its absence is the point.** `/reports` was the
 * case queue with `caseType=CONTENT_REPORT` appended and `/reports/:caseId` was
 * the case detail with a decision panel on it. Two entries in one group, five
 * rows apart, reaching the same records through the same service — an operator
 * had to know which of them carried the actions. The decision moved onto the case
 * detail and the pair of routes went with it, so «الحالات» is now the one
 * destination for everything a subscriber raised.
 *
 * The remaining routes are reached from the surface that owns them — the
 * authoring, edit and asset routes from the content list, the case detail from
 * the case queue, the person and user details from their registers, the editorial
 * detail and edit from the post list, `/pages/contact` from `/pages/about`,
 * `/inbox` from the top bar's
 * notification control, and the three auth screens from before the session
 * exists.
 */

import type { ComponentType, SVGProps } from 'react';

import type { ServiceKey } from '@shared/api/config';
import { holdsTier, type CapabilityMap, type ServiceTier } from '@shared/auth/capabilities';

import {
  BarsIcon,
  CardIcon,
  ClapperboardIcon,
  CogIcon,
  CommentCheckIcon,
  DocumentIcon,
  GaugeIcon,
  LayersIcon,
  LifeRingIcon,
  NewspaperIcon,
  PeopleIcon,
  ShelvesIcon,
  TagIcon,
  TreeIcon,
  UserCheckIcon,
} from '@shared/ui/icons/nav';

/**
 * What an account has to hold for a destination to be worth drawing.
 *
 * One service and one tier, because every console surface is owned by exactly
 * one backend and the entry is only reachable through it. `'*'` asks the same
 * question of any service, for the one surface that fans out over all nine.
 */
export interface NavRequirement {
  service: ServiceKey | '*';
  tier: ServiceTier;
}

export interface NavEntry {
  /** Route path — the URL never carries the pack's bucket segment. */
  to: string;
  label: string;
  /** Icon component, drawn on the 24×24 grid at `stroke-width: 2`. */
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  /**
   * The role this destination needs, in the service that owns it.
   *
   * Transcribed from that service's own route gate, never guessed: `/plans` is
   * `require_operator` in finance and `/payment-gateways` is `require_admin`, so
   * the two differ here exactly as they differ there. An entry whose requirement
   * drifts from its gate is worse than none — it hides a surface the operator
   * may use, which is invisible from this side.
   */
  requires: NavRequirement;
}

export interface NavGroup {
  /**
   * Section caption in the monospace face, deliberately outside the heading
   * hierarchy — it is a caption, not a level. `null` on the console entry, which
   * stands alone above the first group.
   */
  caption: string | null;
  entries: NavEntry[];
}

export const navGroups: readonly NavGroup[] = [
  {
    caption: null,
    entries: [
      {
        to: '/',
        label: 'لوحة التشغيل',
        icon: GaugeIcon,
        requires: { service: 'analytics', tier: 'operator' },
      },
    ],
  },
  {
    caption: 'الفهرس',
    entries: [
      {
        to: '/content',
        label: 'المحتوى',
        icon: ClapperboardIcon,
        requires: { service: 'content', tier: 'operator' },
      },
      {
        to: '/shelves',
        label: 'الرفوف',
        icon: ShelvesIcon,
        requires: { service: 'content', tier: 'operator' },
      },
      {
        to: '/taxonomy/categories',
        label: 'التصنيفات',
        icon: TreeIcon,
        requires: { service: 'content', tier: 'operator' },
      },
      {
        to: '/taxonomy/tags',
        label: 'الوسوم',
        icon: TagIcon,
        requires: { service: 'content', tier: 'operator' },
      },
      {
        to: '/people',
        label: 'المنسوبون',
        icon: PeopleIcon,
        requires: { service: 'content', tier: 'operator' },
      },
    ],
  },
  {
    caption: 'التحرير',
    entries: [
      {
        to: '/editorial',
        label: 'المقالات',
        icon: NewspaperIcon,
        requires: { service: 'content', tier: 'operator' },
      },
    ],
  },
  {
    caption: 'المجتمع',
    entries: [
      {
        to: '/users',
        label: 'المستخدمون',
        icon: UserCheckIcon,
        requires: { service: 'auth', tier: 'operator' },
      },
      {
        to: '/comments',
        label: 'التعليقات',
        icon: CommentCheckIcon,
        requires: { service: 'engagement', tier: 'operator' },
      },
      {
        to: '/cases',
        label: 'الحالات',
        icon: LifeRingIcon,
        requires: { service: 'support', tier: 'operator' },
      },
    ],
  },
  {
    caption: 'الاشتراك',
    entries: [
      { to: '/plans', label: 'الخطط', icon: LayersIcon, requires: { service: 'finance', tier: 'operator' } },
      {
        to: '/subscriptions',
        label: 'تقارير الاشتراك',
        icon: BarsIcon,
        requires: { service: 'analytics', tier: 'operator' },
      },
      {
        to: '/payment-gateways',
        label: 'بوّابات الدفع',
        icon: CardIcon,
        requires: { service: 'finance', tier: 'admin' },
      },
    ],
  },
  {
    caption: 'الموقع',
    entries: [
      {
        to: '/pages/about',
        label: 'الصفحات التعريفية',
        icon: DocumentIcon,
        requires: { service: 'content', tier: 'operator' },
      },
      { to: '/settings', label: 'الإعدادات', icon: CogIcon, requires: { service: '*', tier: 'admin' } },
    ],
  },
];

/**
 * `aria-current` has two values. `page` marks the exact route; a section
 * ancestor stays lit for a sub-route without claiming to be it — the console
 * entry is exact-only, or every route would light it.
 */
export function navEntryState(entryPath: string, pathname: string): 'page' | 'section' | 'idle' {
  if (pathname === entryPath) return 'page';
  if (entryPath !== '/' && pathname.startsWith(`${entryPath}/`)) return 'section';
  return 'idle';
}

/**
 * The navigation, minus what this account cannot use.
 *
 * A group that loses every entry loses its caption too — a heading over nothing
 * reads as a section that failed to load.
 *
 * **Hiding is not protecting.** The route guard redirects and the backend
 * refuses; this only stops the console from advertising fifteen destinations
 * to an account that can open six. An unknown service stays visible, so a
 * console whose capability read failed is the console that shipped before this.
 */
export function visibleNavGroups(capabilities: CapabilityMap): NavGroup[] {
  return navGroups
    .map((group) => ({
      ...group,
      entries: group.entries.filter((entry) =>
        holdsTier(capabilities, entry.requires.service, entry.requires.tier),
      ),
    }))
    .filter((group) => group.entries.length > 0);
}

/**
 * The navigation's own label for the surface at `pathname`, if it names one.
 *
 * The nav table already maps every console destination to the word the operator
 * reads on the screen, so the document's title is derived from it rather than
 * re-declared per route: two lists of the same names drift, and the one nobody
 * looks at drifts first.
 *
 * Longest match wins, so `/taxonomy/tags` is not answered by `/`, and a detail
 * route inherits its section's name — `/users/01M…` is «المستخدمون», which is
 * true and stable where a title carrying an opaque ULID would be neither.
 */
export function navLabelFor(pathname: string): string | undefined {
  const entries = navGroups.flatMap((group) => group.entries);
  return entries
    .filter((entry) => navEntryState(entry.to, pathname) !== 'idle')
    .sort((left, right) => right.to.length - left.to.length)[0]?.label;
}

/** The requirement one route carries, for the guard that redirects rather than hides. */
export function requirementFor(pathname: string): NavRequirement | undefined {
  const entries = navGroups.flatMap((group) => group.entries);
  const match = entries
    .filter((entry) => navEntryState(entry.to, pathname) !== 'idle')
    // Longest path wins, so `/taxonomy/tags` is not answered by `/`.
    .sort((left, right) => right.to.length - left.to.length)[0];

  return match?.requires;
}

/**
 * Whether the console would draw the entry at `to` for this account.
 *
 * The one predicate a surface needs to decide whether to *link* somewhere. A
 * link the guard bounces is worse than no link: it looks like a working
 * destination, and the operator learns nothing from arriving back where they
 * started.
 */
export function canReach(capabilities: CapabilityMap, to: string): boolean {
  const requirement = requirementFor(to);
  return requirement === undefined || holdsTier(capabilities, requirement.service, requirement.tier);
}
